import type { ReactNode } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
} from '@/ui/DropdownMenu';

const INVITE_BY_EMAIL_KEY = 'group_subscriptions.card.invite_by_email';
const BULK_INVITE_CSV_KEY = 'group_subscriptions.card.bulk_invite_csv';

type InviteMenuProps = {
  /** Trigger inner content — label plus any icon. The trigger renders as a button. */
  children: ReactNode;
  triggerClassName: string;
  onInviteByEmail?: () => void;
  onBulkInviteCsv?: () => void;
  /** Header anchors its menu to the trigger end; the centered empty-state
   *  trigger reads better centered. */
  align?: 'start' | 'center' | 'end';
  translations?: Record<string, string>;
};

/**
 * The two-path invite dropdown ("Invite by email address" / "Bulk invite with
 * CSV"), shared by the card header and the no-seats empty state.
 */
export function InviteMenu({
  children,
  triggerClassName,
  onInviteByEmail,
  onBulkInviteCsv,
  align = 'end',
  translations,
}: InviteMenuProps) {
  const inviteByEmailLabel = translations?.[INVITE_BY_EMAIL_KEY] ?? 'Invite by email address';
  const bulkInviteCsvLabel = translations?.[BULK_INVITE_CSV_KEY] ?? 'Bulk invite with CSV';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={triggerClassName}>{children}</DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align={align} sideOffset={4} className="min-w-[200px] py-1.5">
          <DropdownMenuItem onSelect={() => onInviteByEmail?.()}>{inviteByEmailLabel}</DropdownMenuItem>
          <DropdownMenuItem onSelect={() => onBulkInviteCsv?.()}>{bulkInviteCsvLabel}</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}
